'use client';

import { motion } from 'motion/react';
import { useRouter } from 'next/navigation';
import { ChangeEvent, FormEvent, forwardRef, useState } from 'react';
import { FaFilter, FaMapMarkedAlt } from 'react-icons/fa';
import { RiSearchFill } from 'react-icons/ri';
import { twMerge } from 'tailwind-merge';

import { Button } from '@/components/Button';
import { usePageTextContent } from '@/context/pageTextContent';
import { useResponsiveness } from '@/hooks/useResponsiveness';
import { resolvePath } from '@/utils/helpers';
import { Paths } from '@/utils/paths';

import { useSearchPageStore } from '../../useSearchPageStore';

export type ControlProps = {
  className?: string;
};

export const Control = forwardRef<HTMLFormElement, ControlProps>(
  ({ className }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    const near = useSearchPageStore(state => state.near);
    const onNearChange = useSearchPageStore(state => state.onNearChange);
    const toggleMap = useSearchPageStore(state => state.toggleMap);
    const onOpenFiltersModal = useSearchPageStore(
      state => state.onOpenFiltersModal,
    );

    const { isMobile } = useResponsiveness();

    const { content } = usePageTextContent<SearchPageContent>();

    const { searchFieldPlaceholder, mapButton } = content;

    const router = useRouter();

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
      onNearChange(event.target.value);
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const pageUrl = resolvePath({
        pathname: Paths.SEARCH,
        query: { near },
      });

      router.push(pageUrl);
    };

    const onFiltersClick = () => {
      onOpenFiltersModal();
    };

    const onFocus = () => {
      setIsFocused(true);
    };

    const onBlur = () => {
      setIsFocused(false);
    };

    const onMapClick = () => {
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        return;
      }

      toggleMap();
    };

    return (
      <form
        ref={ref}
        className={twMerge(
          'flex w-full flex-row items-center justify-center space-x-10 px-0 md:justify-center md:space-x-10 md:px-0 lg:justify-center lg:space-x-4 lg:px-4',
          className,
        )}
        onSubmit={onSubmit}
      >
        <motion.div
          className="relative h-[2.625rem] max-w-none min-w-[18rem] rounded-lg shadow-[0px_5px_15px_#00000029] md:max-w-none lg:max-w-[16.75rem]"
          animate={isFocused ? { scale: 1.025 } : {}}
        >
          <input
            onFocus={onFocus}
            onBlur={onBlur}
            className="placeholder-gray-550 h-full w-full rounded-lg border-none bg-white px-3 pr-24 placeholder:font-light focus:ring-0 focus:outline-none"
            onChange={onChange}
            value={near}
            placeholder={searchFieldPlaceholder}
          />

          <div className="absolute top-0 right-0 flex h-full w-auto items-center">
            <Button
              aria-label="Search"
              variant="ghost"
              className="hover:bg-transparent"
              type="submit"
            >
              <RiSearchFill className="text-zmyle-primary text-xl" />
            </Button>

            <div className="h-[calc(100%-1rem)] w-px bg-gray-200" />

            <Button
              aria-label="Open filters"
              onClick={onFiltersClick}
              className="h-full bg-white hover:bg-transparent active:bg-transparent"
              variant="ghost"
            >
              <FaFilter className="text-base" />
            </Button>
          </div>
        </motion.div>

        <Button
          onClick={onMapClick}
          variant="ghost"
          className="hover:text-zmyle-primary px-0 text-blue-800 hover:bg-transparent"
        >
          <div className="flex items-center space-x-2">
            <FaMapMarkedAlt className="text-xl" />

            <span className="truncate">{mapButton}</span>
          </div>
        </Button>
      </form>
    );
  },
);

Control.displayName = 'Control';
